import { createClient } from "@supabase/supabase-js";
import { config } from "../config";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

console.log("Supabase URL:", supabaseUrl);
console.log("Supabase Key:", supabaseKey ? "Present" : "Missing");

// Use actual credentials as fallback for development
const fallbackUrl = config.supabaseUrl;
const fallbackKey = config.supabaseAnonKey;

if (!supabaseUrl || !supabaseKey) {
  console.warn(
    "Missing Supabase configuration. Using fallback values for development.",
  );
  console.warn(
    "Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY environment variables.",
  );
}

// Create a single supabase client for interacting with your database
export const supabase = createClient(
  supabaseUrl || fallbackUrl,
  supabaseKey || fallbackKey,
  {
    auth: {
      persistSession: true,
      storageKey: "ascend-auth-token",
      storage: typeof window !== "undefined" ? window.localStorage : undefined,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  },
);

// Safety shim: ensure only one channel instance exists per name
try {
  const originalChannel = (supabase as any).channel.bind(supabase);
  (supabase as any).channel = (name: string, options?: any) => {
    try {
      const existing = (supabase as any).getChannels?.().find((ch: any) => {
        // supabase-js v2 exposes topic or channelName on realtime channel
        const topic = ch?.topic || ch?.channelName || ch?.params?.channel || ch?.params?.channel_name;
        return topic === name;
      });
      if (existing) {
        (supabase as any).removeChannel(existing);
      }
    } catch (error) {
      console.warn('Error managing channel:', error);
    }
    return originalChannel(name, options);
  };
} catch {}

interface UploadFileParams {
  bucket: string;
  file: File;
  userId: string;
  onProgress?: (progress: number) => void;
}

export const uploadFile = async ({
  bucket,
  file,
  userId,
  onProgress,
}: UploadFileParams): Promise<string> => {
  try {
    const fileExt = file.name.split(".").pop();
    const fileName = `${userId}/${Date.now()}.${fileExt}`;

    const { error: uploadError, data } = await supabase.storage
      .from(bucket)
      .upload(fileName, file, {
        upsert: true,
      });

    if (uploadError) {
      throw uploadError;
    }

    const {
      data: { publicUrl },
    } = supabase.storage.from(bucket).getPublicUrl(fileName);

    return publicUrl;
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
};

// Log initialization status
console.log(
  "Supabase client initialized with URL:",
  supabaseUrl || fallbackUrl,
);
